import { createContext, useContext, useReducer, useRef, useEffect } from 'react';
import { MatchActionsResponse, School } from 'types/connectTypes';
import {
  ConnectContextType,
  ConnectContextActionType,
  ConnectContextStateInterface,
  ConnectContextProviderInterface,
} from 'features/Connect/features/Context/types';
import {
  ConnectContextActionTypes,
  ConnectModals,
  MatchQuizStatusStrings,
} from 'features/Connect/utils/connectEnums';
import CandidateMatchingAPI from 'features/Connect/api/candidateMatchingAPI';
import { ConnectMatchingAPIViewTypes } from 'features/Connect/api/constants';
import { useRouteMatch } from 'react-router-dom';
import { User } from 'types';
import CandidateMatchQuizAPI from 'features/Connect/api/candidateMatchQuizAPI';
import auth from 'utils/auth';
import UserSavedSchoolAPI from 'features/Connect/api/userSavedSchoolAPI';

export const connectContextInitialState: ConnectContextStateInterface = {
  contactedSchoolIds: {},
  connectModalsOpenState: {
    [ConnectModals.AUTHENTICATED_ADMIN_MODAL]: false,
    [ConnectModals.EXPRESS_INTEREST_EXIT_MODAL]: false,
    [ConnectModals.EXPRESS_INTEREST_MODAL]: false,
    [ConnectModals.MATCH_QUIZ_ACCESS_MODAL]: false,
    [ConnectModals.READY_TO_CONNECT_MODAL]: false,
    [ConnectModals.QUICK_APPLY_DISTRICT_JOBBOARD_REDIRECT_MODAL]: false,
  },
  isLoading: true,
  isMoreInfoSliderOpen: false,
  isUserAuthenticated: false,
  savedSchoolIds: {},
  selectedSchool: null,
  selectedVacancyId: '0',
  candidateMatches: {
    total: 0,
    pending: [],
    accepted: [],
    rejected: [],
    previously_actioned: [],
  },
  hasInitiallyFetchedMatches: false,
  isFetchingMatches: true,
  isFetchingMatchQuizStatus: false,
  matchQuizStatus: null,
  jobSeekingStatus: null,
  jobSeekingStatusUpdatedAt: null,
  isFetchingSavedSchoolsList: false,
  districtSlugs: {},
};

const connectContextReducer = (
  connectContextState: ConnectContextStateInterface,
  connectContextActions: ConnectContextActionType
): ConnectContextStateInterface => {
  switch (connectContextActions.type) {
    case ConnectContextActionTypes.SET_CANDIDATE_MATCHES:
      return {
        ...connectContextState,
        candidateMatches: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_CONTACTED_SCHOOL_IDS:
      return {
        ...connectContextState,
        contactedSchoolIds: {
          ...connectContextState.contactedSchoolIds,
          [connectContextActions.payload]: true,
        },
      };

    case ConnectContextActionTypes.SET_IS_MODAL_OPEN: {
      const { connectModalName, setIsModalOpen } = connectContextActions.payload;
      return {
        ...connectContextState,
        connectModalsOpenState: {
          ...connectContextState.connectModalsOpenState,
          [connectModalName]: setIsModalOpen,
        },
      };
    }

    case ConnectContextActionTypes.SET_IS_LOADING:
      return {
        ...connectContextState,
        isLoading: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_IS_USER_AUTHENTICATED:
      return {
        ...connectContextState,
        isUserAuthenticated: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_SELECTED_SCHOOL:
      return {
        ...connectContextState,
        selectedSchool: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_SELECTED_VACANCY_ID:
      return {
        ...connectContextState,
        selectedVacancyId: connectContextActions.payload,
      };

    case ConnectContextActionTypes.TOGGLE_SAVED_SCHOOL_IDS: {
      const schoolId = connectContextActions.payload;
      const newSavedSchoolIds = { ...connectContextState.savedSchoolIds };
      if (newSavedSchoolIds[schoolId]) {
        delete newSavedSchoolIds[schoolId];
      } else {
        newSavedSchoolIds[schoolId] = true;
      }
      return {
        ...connectContextState,
        savedSchoolIds: newSavedSchoolIds,
      };
    }
    case ConnectContextActionTypes.SET_SAVED_SCHOOL_IDS:
      return {
        ...connectContextState,
        savedSchoolIds: connectContextActions.payload,
      };
    case ConnectContextActionTypes.SET_IS_FETCHING_MATCHES:
      return {
        ...connectContextState,
        isFetchingMatches: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_HAS_INITIALLY_FETCHED_MATCHES:
      return {
        ...connectContextState,
        hasInitiallyFetchedMatches: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_IS_MORE_INFO_SLIDER_OPEN:
      return {
        ...connectContextState,
        isMoreInfoSliderOpen: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS:
      return {
        ...connectContextState,
        isFetchingMatchQuizStatus: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_MATCH_QUIZ_STATUS:
      return {
        ...connectContextState,
        matchQuizStatus: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_JOB_SEEKING_STATUS:
      return {
        ...connectContextState,
        jobSeekingStatus: connectContextActions.payload.status,
        jobSeekingStatusUpdatedAt: connectContextActions.payload.updated_at,
      };

    case ConnectContextActionTypes.SET_IS_FETCHING_SAVED_SCHOOLS_LIST:
      return {
        ...connectContextState,
        isFetchingSavedSchoolsList: connectContextActions.payload,
      };

    case ConnectContextActionTypes.SET_DISTRICT_SLUG:
      return {
        ...connectContextState,
        districtSlugs: {
          ...connectContextState.districtSlugs,
          [connectContextActions.payload.ncesId]: connectContextActions.payload.slug,
        },
      };

    default:
      return connectContextState;
  }
};

const ConnectContext = createContext<ConnectContextType | undefined>(undefined);

export const ConnectContextProvider: React.FC<ConnectContextProviderInterface> = ({ children }) => {
  const user = auth.getUser();
  const isUserAuthenticated = auth.loggedIn();
  const [connectContextState, dispatch] = useReducer(
    connectContextReducer,
    connectContextInitialState
  );

  const isMounted = useRef(true);
  const hasFetchedRef = useRef(false); // Add this to track if we've ever fetched

  const isConnectJobboard = useRouteMatch('/connect/:stateCode');
  const isCandidatePortal = useRouteMatch('/candidate/');
  const shouldFetchMatchesAndQuizStatusAndSavedSchools = !!isConnectJobboard || !!isCandidatePortal;

  const connectContextActions = {
    hasSchoolBeenContacted: (schoolId: string): boolean => {
      return !!connectContextState.contactedSchoolIds[schoolId];
    },
    hasSchoolBeenSaved: (schoolId: string): boolean => {
      return !!connectContextState.savedSchoolIds[schoolId];
    },
    setCandidateMatches: (candidateMatches: MatchActionsResponse) =>
      dispatch({
        type: ConnectContextActionTypes.SET_CANDIDATE_MATCHES,
        payload: candidateMatches,
      }),
    setContactedSchoolIds: (schoolId: string) =>
      dispatch({ type: ConnectContextActionTypes.SET_CONTACTED_SCHOOL_IDS, payload: schoolId }),
    setModalOpen: (connectModalName: string, setIsModalOpen: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_MODAL_OPEN,
        payload: { connectModalName, setIsModalOpen },
      }),
    setIsLoading: (isLoading: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_LOADING,
        payload: isLoading,
      }),
    setIsUserAuthenticated: (isAuthenticated: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_USER_AUTHENTICATED,
        payload: isAuthenticated,
      }),
    setSavedSchoolIds: (savedSchoolIds: Record<string, boolean>) =>
      dispatch({
        type: ConnectContextActionTypes.SET_SAVED_SCHOOL_IDS,
        payload: savedSchoolIds,
      }),
    setSelectedSchool: (school: School) =>
      dispatch({
        type: ConnectContextActionTypes.SET_SELECTED_SCHOOL,
        payload: school,
      }),
    setSelectedVacancyId: (selectedVacancyId: string) =>
      dispatch({
        type: ConnectContextActionTypes.SET_SELECTED_VACANCY_ID,
        payload: selectedVacancyId,
      }),
    toggleSavedSchoolIds: (schoolId: string) =>
      dispatch({ type: ConnectContextActionTypes.TOGGLE_SAVED_SCHOOL_IDS, payload: schoolId }),
    setHasInitiallyFetchedMatches: (hasFetched: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_HAS_INITIALLY_FETCHED_MATCHES,
        payload: hasFetched,
      }),
    setIsFetchingMatches: (isFetching: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_MATCHES,
        payload: isFetching,
      }),
    setIsMoreInfoSliderOpen: (isOpen: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_MORE_INFO_SLIDER_OPEN,
        payload: isOpen,
      }),
    fetchCandidateMatches: (user: User) => fetchMatches(user),
    setMatchQuizStatus: (matchQuizStatus: string) =>
      dispatch({
        type: ConnectContextActionTypes.SET_MATCH_QUIZ_STATUS,
        payload: matchQuizStatus,
      }),
    setIsFetchingMatchQuizStatus: (isFetching: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS,
        payload: isFetching,
      }),
    setJobSeekingStatus: (jobSeekingStatus: { status: string; updated_at: string }) =>
      dispatch({
        type: ConnectContextActionTypes.SET_JOB_SEEKING_STATUS,
        payload: jobSeekingStatus,
      }),
    setIsFetchingSavedSchoolsList: (isLoading: boolean) =>
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_SAVED_SCHOOLS_LIST,
        payload: isLoading,
      }),
    setDistrictSlug: (ncesId: string, slug: string) =>
      dispatch({
        type: ConnectContextActionTypes.SET_DISTRICT_SLUG,
        payload: { ncesId, slug },
      }),
    getDistrictSlug: (ncesId: string) => connectContextState.districtSlugs[ncesId],
  };

  const fetchMatches = async (user: User) => {
    if (user?.profile && shouldFetchMatchesAndQuizStatusAndSavedSchools) {
      hasFetchedRef.current = true;
      connectContextActions.setIsFetchingMatches(true);

      try {
        const viewType = isCandidatePortal
          ? ConnectMatchingAPIViewTypes.FULL
          : ConnectMatchingAPIViewTypes.BASIC;

        const matches = await CandidateMatchingAPI.getMatches(viewType).catch(() => ({
          total: 0,
          pending: [],
          accepted: [],
          rejected: [],
          previously_actioned: [],
        }));

        if (isMounted.current) {
          connectContextActions.setCandidateMatches(matches);
          connectContextActions.setHasInitiallyFetchedMatches(true);
          connectContextActions.setIsUserAuthenticated(!!user.profile);
        }
      } finally {
        connectContextActions.setIsFetchingMatches(false);
      }
    }
  };

  const fetchMatchQuizStatus = async (user: User) => {
    if (user?.id) {
      dispatch({
        type: ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS,
        payload: true,
      });
      try {
        const response = await CandidateMatchQuizAPI.getMatchQuizStatus(user.id);
        const status = response?.status || MatchQuizStatusStrings.NOT_TAKEN;
        if (isMounted.current) {
          dispatch({ type: ConnectContextActionTypes.SET_MATCH_QUIZ_STATUS, payload: status });
        }
      } catch (e) {
        console.error('Error fetching match quiz status:', e);
      } finally {
        dispatch({
          type: ConnectContextActionTypes.SET_IS_FETCHING_MATCH_QUIZ_STATUS,
          payload: false,
        });
      }
    }
  };

  const fetchSavedSchools = () => {
    if (isUserAuthenticated) {
      connectContextActions.setIsFetchingSavedSchoolsList(true);
      UserSavedSchoolAPI.getSchoolsUserHasSaved()
        .then((savedSchools) => {
          const savedSchoolIds: Record<string, boolean> = {};
          savedSchools.forEach((school) => {
            if (school.school_nces_id) {
              savedSchoolIds[school.school_nces_id] = true;
            }
          });
          connectContextActions.setSavedSchoolIds(savedSchoolIds);
        })
        .finally(() => {
          connectContextActions.setIsFetchingSavedSchoolsList(false);
        });
    }
  };

  useEffect(() => {
    if (shouldFetchMatchesAndQuizStatusAndSavedSchools) {
      fetchMatchQuizStatus(user);
      fetchSavedSchools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.preference?.job_seeking_status && user?.preference?.job_seeking_status_updated_at) {
      connectContextActions.setJobSeekingStatus({
        status: user.preference.job_seeking_status,
        updated_at: user.preference.job_seeking_status_updated_at,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <ConnectContext.Provider value={{ ...connectContextState, connectContextActions }}>
      {children}
    </ConnectContext.Provider>
  );
};

export const useConnectContext = () => {
  const context = useContext(ConnectContext);
  if (context === undefined) {
    throw new Error('useConnectContext must be used within an ConnectContextProvider');
  }
  return context;
};
